import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { normalizeDriveLabel, DRIVE_KINDS, DRIVE_STATUSES, type DriveRow, type DriveInput, type DriveProject, type DriveStatus } from '../../shared/drives';
import { melbourneDate } from '../../shared/dates';
import { api, ApiError } from '../lib/api';
import { Modal, Seg, formatProjectNumber, formatDayMonth } from './primitives';
import { Sheet, SheetButton } from './Sheet';
import { useIsMobile } from '../lib/useIsMobile';

export function DriveEditor({ initial, seedLabel = '', editingDriveLabel = null, onClose, onSaved, onDeleted }: {
  /** Label of another drive currently flagged as the editing drive, if any. */
  initial?: DriveRow; seedLabel?: string; editingDriveLabel?: string | null; onClose: () => void;
  onSaved: (drive: DriveRow) => void | Promise<void>; onDeleted?: () => void;
}) {
  const formId = useId();
  const isMobile = useIsMobile();
  const [form, setForm] = useState<DriveInput>(() => initial ?? {
    label: seedLabel, free_gb: null, kind: 'hdd', capacity_gb: null, location: null, serial: null,
    purchased_on: null, status: 'active', notes: null,
  });
  const [freeTouched, setFreeTouched] = useState(false);
  const [editingDrive, setEditingDrive] = useState(!!initial?.is_editing_drive);
  const [projects, setProjects] = useState<DriveProject[]>([]);
  const [projectsError, setProjectsError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [forceRequired, setForceRequired] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const close = () => { if (!busy) onClose(); };
  useEffect(() => {
    if (!initial) return;
    let live = true;
    api<{ projects: DriveProject[] }>(`/api/admin/drives/${initial.id}/projects`)
      .then((r) => { if (live) setProjects(r.projects); })
      .catch((e: Error) => { if (live) setProjectsError(e.message); });
    return () => { live = false; };
  }, [initial?.id]);
  useEffect(() => {
    if (isMobile) return; // The portal-based Sheet owns focus on phones.
    const previous = document.activeElement as HTMLElement | null;
    const modal = host.current?.querySelector<HTMLElement>('[role="dialog"]');
    modal?.querySelector<HTMLInputElement>('input')?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modal) return;
      const elements = [...modal.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]')];
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', trap);
    return () => { document.removeEventListener('keydown', trap); previous?.focus(); };
  }, [isMobile]);
  function field<K extends keyof DriveInput>(key: K, value: DriveInput[K]) { if (key === 'free_gb') setFreeTouched(true); setForm((f) => ({ ...f, [key]: value })); }
  async function save(status?: DriveStatus) {
    if (busy) return;
    setBusy(true); setError('');
    // The flag is only sent when it changes; leaving active status clears it server-side.
    const flag = (status ?? form.status) === 'active' && !initial?.auto_backup && editingDrive;
    try {
      const { drive } = await api<{ drive: DriveRow }>(`/api/admin/drives${initial ? `/${initial.id}` : ''}`, {
        method: initial ? 'PATCH' : 'POST', json: { label: form.label, kind: form.kind, capacity_gb: form.capacity_gb,
          location: form.location, serial: form.serial, purchased_on: form.purchased_on, notes: form.notes, status: status ?? form.status,
          ...(!initial || freeTouched ? { free_gb: form.free_gb ?? null } : {}),
          ...(flag !== !!initial?.is_editing_drive && (status ?? form.status) === 'active' ? { is_editing_drive: flag } : {}),
        },
      });
      await onSaved(drive);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!initial || busy) return;
    setBusy(true); setError('');
    try {
      await api(`/api/admin/drives/${initial.id}${forceRequired ? '?force=1' : ''}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setForceRequired(true);
      setError((e as Error).message);
    } finally { setBusy(false); }
  }
  const editingBlocked = initial?.auto_backup ? "Automatic backup drives can't be the editing drive." : form.status !== 'active' ? 'Only an active drive can be the editing drive.' : null;
  const editingOn = !editingBlocked && editingDrive;
  const content = <>
      <form id={formId} className="drive-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <fieldset disabled={busy} className="drive-form-grid">
          <label className="f-field span-2"><span className="f-label">Label *</span><input className="f-input mono" required value={form.label} onChange={(e) => field('label', e.target.value)} /></label>
          <div className="f-field drive-kind" role="group" aria-label="Kind"><span className="f-label">Kind</span><Seg options={DRIVE_KINDS} value={form.kind} onChange={(kind) => field('kind', kind)} /></div>
          <label className="f-field"><span className="f-label">Capacity (GB)</span><input className="f-input mono" type="number" min="0" step="1" value={form.capacity_gb ?? ''} onChange={(e) => field('capacity_gb', e.target.value === '' ? null : Number(e.target.value))} /></label>
          <label className="f-field"><span className="f-label">Free space (GB)</span><input className="f-input mono" type="number" min="0" step="1" value={form.free_gb ?? ''} onChange={(e) => field('free_gb', e.target.value === '' ? null : Number(e.target.value))} />
            {initial?.free_checked_at != null && <span className="reg-sub">Checked {formatDayMonth(melbourneDate(initial.free_checked_at))}</span>}
          </label>
          <label className="f-field"><span className="f-label">Location</span><input className="f-input" value={form.location ?? ''} onChange={(e) => field('location', e.target.value || null)} /></label>
          <label className="f-field"><span className="f-label">Serial</span><input className="f-input mono" value={form.serial ?? ''} onChange={(e) => field('serial', e.target.value || null)} /></label>
          <label className="f-field"><span className="f-label">Purchased on</span><input className="f-input" type="date" value={form.purchased_on ?? ''} onChange={(e) => field('purchased_on', e.target.value || null)} /></label>
          <label className="f-field"><span className="f-label">Status</span><select className="f-select" value={form.status} onChange={(e) => field('status', e.target.value as DriveStatus)}>{DRIVE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></label>
          <div className="f-field span-2 drive-editing-field"><span className="f-label">Editing drive</span>
            <label className={`drive-toggle-line${editingBlocked ? ' disabled' : ''}`}>
              <span className="drive-toggle-wrap"><input type="checkbox" checked={editingOn} disabled={!!editingBlocked} onChange={(e) => setEditingDrive(e.target.checked)} aria-label={`Use ${form.label || 'this drive'} as the editing drive`} /><span className={`toggle sm${editingOn ? ' on' : ''}`} aria-hidden="true" /></span>
              <span>{editingOn ? 'This is the editing drive' : 'Use as the editing drive'}</span>
            </label>
            <span className="reg-sub">{editingBlocked ?? <>Pinned first on <Link to="/drives">Drives → Active</Link> and always open. One drive at a time{editingDriveLabel && !editingOn ? ` — switching this on clears it from ${editingDriveLabel}.` : '.'}</>}</span>
          </div>
          <label className="f-field span-2"><span className="f-label">Notes</span><textarea className="f-textarea" rows={3} value={form.notes ?? ''} onChange={(e) => field('notes', e.target.value || null)} /></label>
        </fieldset>
      </form>
      {initial && <div className="drawer-links">
        <div className="field-label">Linked projects · {projects.length}</div>
        {projectsError && <p role="alert">{projectsError}</p>}
        {!projects.length && !projectsError && <p className="reg-sub">No linked projects.</p>}
        {projects.map((p) => <Link className="lr" key={p.project_id} to={`/projects/${p.project_id}`}><span className="num">{formatProjectNumber(p.project_number)}</span><span className="drive-project-name">{p.name}</span><span className="role">{p.role}</span></Link>)}
      </div>}
      {error && <p className="drive-error" role="alert">{error}</p>}
      {initial && onDeleted && <div className="drive-delete-action">
        <button className="btn danger" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete drive</button>
      </div>}
      {confirmDelete && <div className="drive-delete-confirm" role="group" aria-label="Confirm drive deletion">
        <p>{forceRequired ? 'Delete this drive and unlink its live projects? Trashed projects keep their archive history.' : 'Delete this drive from the register?'}</p>
        <button className="btn" disabled={busy} onClick={() => { setConfirmDelete(false); setForceRequired(false); }}>Keep drive</button>
        <button className="btn danger" disabled={busy} onClick={() => void remove()}>{forceRequired ? 'Delete and unlink live projects' : 'Confirm delete'}</button>
      </div>}
  </>;
  const statusActions = initial && <div className="drive-editor-actions" role="group" aria-label="Drive status actions">
    <button className={isMobile ? 'cbtn' : 'drive-retire-action'} disabled={busy || form.status === 'retired'} onClick={() => void save('retired')}>Mark retired</button>
    {isMobile && <button className="cbtn" disabled={busy || form.status === 'lost'} onClick={() => void save('lost')}>Mark lost</button>}
  </div>;
  const saveDisabled = busy || !normalizeDriveLabel(form.label);
  const title = initial ? 'Edit drive' : 'Add drive';
  return <div className="drive-editor-host" ref={host}>
    {isMobile ? <Sheet open variant="full" title={title} context="Registers · drives" onClose={close} className="sheet-modal drive-editor-sheet"
      leftAction={<SheetButton disabled={busy} onClick={close}>Cancel</SheetButton>}
      rightAction={<button className="sheet-act-btn accent" form={formId} type="submit" disabled={saveDisabled}>{busy ? 'Saving…' : 'Save drive'}</button>}
      footer={statusActions}>
      <div className="modal-body">{content}</div>
    </Sheet> : <Modal title={title} context="Registers · drives" width={440} onClose={close}
      foot={<>{statusActions}<button className="btn" disabled={busy} onClick={close}>Cancel</button><button className="btn primary" form={formId} type="submit" disabled={saveDisabled}>{busy ? 'Saving…' : 'Save drive'}</button></>}>
      {content}
    </Modal>}
  </div>;
}
